import {
  createContext,
  ReactNode,
  RefObject,
  useCallback,
  useContext,
} from "react";

const PageScrollContext = createContext<RefObject<HTMLElement> | null>(null);

/** Marks `containerRef` as the element that scrolls the page's content, for
 * layouts where that is not the window. */
export function PageScrollProvider({
  containerRef,
  children,
}: {
  containerRef: RefObject<HTMLElement>;
  children: ReactNode;
}) {
  return (
    <PageScrollContext.Provider value={containerRef}>
      {children}
    </PageScrollContext.Provider>
  );
}

/** Returns a function that scrolls the page's content back to the top,
 * instantly unless asked otherwise. Use it instead of `window.scrollTo`:
 * inside a layout whose content scrolls in its own container, the window may
 * not scroll at all. Outside a provider it scrolls the window. */
export function useScrollPageToTop() {
  const containerRef = useContext(PageScrollContext);

  return useCallback(
    // "instant" rather than "auto": the root sets `scroll-behavior: smooth`,
    // which "auto" would inherit.
    (behavior: ScrollBehavior = "instant") => {
      // Both are scrolled because which one actually scrolls can change with
      // the viewport width; scrolling the one that doesn't is a no-op.
      containerRef?.current?.scrollTo({ top: 0, behavior });
      window.scrollTo({ top: 0, behavior });
    },
    [containerRef],
  );
}
